/**
 * Scheduling Availability Module
 *
 * Centralized availability resolution and validation logic for Waitlist v1.
 *
 * Used by:
 * - Offer creation (adminNotifications.ts)
 * - Future booking flows
 * - UI components (availability pickers, validation)
 *
 * Does NOT include:
 * - Capacity/concurrency
 * - Multi-provider UI
 * - Calendar sync
 */

import { supabase } from '@/integrations/supabase/client';

// ============================================================================
// Types
// ============================================================================

export interface TimeRange {
  start: string; // HH:mm format (24-hour), e.g., "09:00", "14:30"
  end: string;   // HH:mm format (24-hour), e.g., "17:00", "23:00"
}

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

// ============================================================================
// Core Availability Resolution
// ============================================================================

/**
 * Get available time ranges for a specific date and provider
 *
 * Resolution order:
 * 1. Check for date-specific override (takes precedence)
 *    - If 'closed' → return [] (zero availability)
 *    - If 'custom_hours' → return custom time ranges
 * 2. Fall back to weekly hours
 *    - If day disabled → return []
 *    - Otherwise return single range from weekly hours
 *
 * HARD INVARIANT: Date override with 'closed' returns [] (weekly hours NOT consulted)
 *
 * @param businessId - Business UUID
 * @param providerId - Provider UUID (null = default provider for v1)
 * @param date - Date string in YYYY-MM-DD format
 * @returns Array of available time ranges (empty if unavailable)
 */
export async function getAvailabilityForDate(
  businessId: string,
  providerId: string | null,
  date: string // YYYY-MM-DD
): Promise<TimeRange[]> {

  // STEP 1: Check for date-specific override (provider-scoped)
  let overrideQuery = supabase
    .from('business_date_overrides')
    .select('id, override_mode')
    .eq('business_id', businessId)
    .eq('override_date', date);

  // Handle NULL provider_id correctly (eq doesn't work with NULL)
  if (providerId === null) {
    overrideQuery = overrideQuery.is('provider_id', null);
  } else {
    overrideQuery = overrideQuery.eq('provider_id', providerId);
  }

  const { data: override, error: overrideError } = await overrideQuery.maybeSingle();

  if (overrideError) {
    console.error('Error fetching date override:', overrideError);
    // Graceful degradation: if override fetch fails, fall through to weekly hours
  }

  if (override) {
    // HARD INVARIANT: closed = zero availability (no fallback to weekly hours)
    if (override.override_mode === 'closed') {
      return [];
    }

    // custom_hours: fetch and return time ranges
    if (override.override_mode === 'custom_hours') {
      const { data: ranges, error: rangesError } = await supabase
        .from('business_date_time_ranges')
        .select('start_time, end_time')
        .eq('override_id', override.id)
        .order('sort_order', { ascending: true });

      if (rangesError) {
        console.error('Error fetching time ranges:', rangesError);
        return []; // Fail closed: if ranges fetch fails, no availability
      }

      return ranges?.map(r => ({
        start: r.start_time, // Already in HH:mm format (validated by DB CHECK)
        end: r.end_time
      })) || [];
    }
  }

  // STEP 2: Fall back to weekly hours (provider-scoped)
  const dayOfWeek = getDayOfWeek(date);

  let hoursQuery = supabase
    .from('business_hours')
    .select('enabled, start_time, end_time')
    .eq('business_id', businessId)
    .eq('day_of_week', dayOfWeek);

  // Handle NULL provider_id correctly (eq doesn't work with NULL)
  if (providerId === null) {
    hoursQuery = hoursQuery.is('provider_id', null);
  } else {
    hoursQuery = hoursQuery.eq('provider_id', providerId);
  }

  const { data: weeklyHours, error: hoursError } = await hoursQuery.maybeSingle();

  if (hoursError) {
    console.error('Error fetching weekly hours:', hoursError);
    return []; // Fail closed: if hours fetch fails, no availability
  }

  if (!weeklyHours || !weeklyHours.enabled) {
    return []; // Not available on this weekday
  }

  // Return single range from weekly hours (already in HH:mm format)
  return [{
    start: weeklyHours.start_time,
    end: weeklyHours.end_time
  }];
}

// ============================================================================
// Service Duration Validation
// ============================================================================

/**
 * Check if service duration fits within available time ranges
 *
 * Requirements:
 * - Service must fit entirely within ONE range (no spanning across gaps)
 * - Start time must be >= range start
 * - End time (start + duration) must be <= range end
 *
 * Normalizes input time to HH:mm before validation.
 *
 * @param availability - Array of available time ranges
 * @param requestedTime - Requested start time (any format: "2:30 PM", "14:30", etc.)
 * @param serviceDuration - Service duration in minutes
 * @returns true if service fits within any single range, false otherwise
 */
export function canServiceFit(
  availability: TimeRange[],
  requestedTime: string,
  serviceDuration: number
): boolean {

  if (availability.length === 0) {
    return false; // No availability
  }

  // Normalize to HH:mm (24-hour format)
  const normalizedTime = normalizeTime(requestedTime);

  // Convert to minutes since midnight
  const startMinutes = timeToMinutes(normalizedTime);
  const endMinutes = startMinutes + serviceDuration;

  // Check if appointment fits entirely within any single range
  for (const range of availability) {
    const rangeStart = timeToMinutes(range.start); // Already HH:mm
    const rangeEnd = timeToMinutes(range.end);

    // Must start after range opens AND end before range closes
    if (startMinutes >= rangeStart && endMinutes <= rangeEnd) {
      return true; // ✅ Fits!
    }
  }

  return false; // ❌ Doesn't fit in any range
}

// ============================================================================
// Time Normalization and Utilities
// ============================================================================

/**
 * Normalize time to canonical HH:mm format (24-hour)
 *
 * Handles various input formats:
 * - "2:30 PM" → "14:30"
 * - "14:30" → "14:30" (already normalized)
 * - "9:00am" → "09:00"
 * - "9:00" → "09:00" (pad single-digit hour)
 *
 * @param time - Time string in any common format
 * @returns Time in HH:mm format (24-hour)
 */
export function normalizeTime(time: string): string {
  // Already in HH:mm format
  if (/^\d{2}:\d{2}$/.test(time)) {
    return time;
  }

  // Parse AM/PM format (e.g., "2:30 PM", "9:00am")
  const ampmMatch = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampmMatch) {
    let hours = parseInt(ampmMatch[1], 10);
    const minutes = ampmMatch[2];
    const period = ampmMatch[3].toUpperCase();

    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }

  // Single-digit hour format (e.g., "9:00")
  const singleMatch = time.match(/^(\d{1}):(\d{2})$/);
  if (singleMatch) {
    return `0${singleMatch[1]}:${singleMatch[2]}`;
  }

  // Fallback: return as-is (validation will fail later)
  console.warn(`Unable to normalize time: ${time}`);
  return time;
}

/**
 * Convert HH:mm time to minutes since midnight
 *
 * Used for numerical time comparisons.
 *
 * @param time - Time in HH:mm format
 * @returns Minutes since midnight (0-1439)
 */
export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Get day of week from YYYY-MM-DD date string
 *
 * Treats date as timezone-agnostic wall-time per business_date invariant.
 * Uses UTC methods to ensure consistent results regardless of client timezone.
 *
 * @param date - Date string in YYYY-MM-DD format (wall-time, no timezone)
 * @returns Day of week as lowercase string (e.g., "monday", "tuesday")
 */
export function getDayOfWeek(date: string): DayOfWeek {
  // Parse date components manually to avoid timezone issues
  const [year, month, day] = date.split('-').map(Number);

  // Create UTC date to ensure consistent day-of-week calculation
  // regardless of client timezone (date is wall-time, not UTC)
  const d = new Date(Date.UTC(year, month - 1, day));
  const days: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[d.getUTCDay()];
}
